import { Component, HostListener, OnInit, TemplateRef } from "@angular/core";
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  FormControl,
  Validators,
} from "@angular/forms";
import { AbstractControl, ValidatorFn } from "@angular/forms";
import Swal from "sweetalert2/dist/sweetalert2.js";
import { MatDialog } from "@angular/material/dialog";
import { TranslateService } from "@ngx-translate/core";
import { ToastrService } from "ngx-toastr";
import { marker as TRANSLATE_ME } from "@biesbjerg/ngx-translate-extract-marker";
import { CrudService } from "../../Services/crud.service";
import { LogedInUserService } from "../../Services/loged-in-user.service";
import { DeleteConfirmationDialogComponent } from "../delete-confirmation-dialog/delete-confirmation-dialog.component";
import { CanDeactivateGuard } from "src/app/guards/can-deactivate.guard";
import { DeactiveConfirmComponent } from "../deactive-confirm/deactive-confirm.component";
import { NgbModal, ModalDismissReasons } from "@ng-bootstrap/ng-bootstrap";
import { ROLE } from "src/app/Utils/const";
import { FormStateService } from "src/app/Services/form.service";

@Component({
  selector: "app-user-management",
  templateUrl: "./user-management.component.html",
  standalone: false,
  styles: [
    `
      ::ng-deep .custom-close-button {
        padding: 0.25rem 0.5rem;
        font-size: 25px;
        line-height: 1;
        border: none !important;
        top: 1rem;
        right: 1rem;
        align-items: center;
        justify-content: center;
        background-color: transparent !important;
        color: black !importantl;
      }
      ::ng-deep .modal .modal-dialog .modal-content .modal-header button {
        color: #000 !important;
        background-color: transparent !important;
        border: none !important;
        font-size: 25px !important;
      }
      ::ng-deep .mat-mdc-menu-panel {
        min-width: 50px !important;
      }
      .tooltip {
        z-index: 3000 !important;
        position: fixed !important; // prevents it from being clipped by menu overlay
      }
    `,
  ],
})
export class UserManagementComponent implements OnInit {
  form: UntypedFormGroup;
  state = false;
  submitted = false;
  showForm = false;
  data: [];
  userManagementId: any;
  currentUser;
  isWriter: boolean = false;
  isReviewer: boolean = false;
  isPublisher: boolean = false;
  isAdmin: boolean = false;
  isTranslator: boolean = false;
  Role;
  userList = [];
  showRoleError = false;
  userId;
  DEAULT_ERROR_MESSAGE = {
    NAME: "Name is require.",
    EMAIL: "Email is require.",
  };

  closeResult = "";

  open(content: TemplateRef<any>) {
    this.modalService
      .open(content, {
        ariaLabelledBy: "modal-basic-title",
        backdrop: "static",
        keyboard: false,
      })
      .result.then(
        (result) => {
          this.closeResult = `Closed with: ${result}`;
        },
        (reason) => {
          this.closeResult = `Dismissed ${this.getDismissReason(reason)}`;
        }
      );
  }

  private getDismissReason(reason: any): string {
    switch (reason) {
      case ModalDismissReasons.ESC:
        return "by pressing ESC";
      case ModalDismissReasons.BACKDROP_CLICK:
        return "by clicking on a backdrop";
      default:
        return `with: ${reason}`;
    }
  }

  constructor(
    private translateService: TranslateService,
    private formBuilder: UntypedFormBuilder,
    private toastr: ToastrService,
    private crudService: CrudService,
    private logedInUserService: LogedInUserService,
    private dialog: MatDialog,
    private modalService: NgbModal,
    private formStateService: FormStateService
  ) {
    this.translateService.setDefaultLang("en");
    let language = localStorage.getItem("language")
      ? localStorage.getItem("language")
      : "en";
    this.translateService.use(language);
    this.createUserManagement = this.createUserManagement.bind(this);
    this.getUserManagementData = this.getUserManagementData.bind(this);
    this.getUserManagementData();
    this.getUserList = this.getUserList.bind(this);
    this.getUserList();
    this.Role = ROLE.reduce((acc, role) => {
      const [key, value] = Object.entries(role)[0];
      acc[key] = value;
      return acc;
    }, {});
    // console.log("role here", this.Role);
  }

  getUserManagementData() {
    let self = this;
    self.logedInUserService.get(function (logedInUser) {
      self.crudService.get(
        null,
        function (response) {
          self.data = response;
        },
        'userManagements?filter={"where":{"companyId":"' +
          logedInUser.companyId +
          '"}}'
      );
    });
  }

  ngOnInit() {
    this.form = this.formBuilder.group({
      username: ["", Validators.required, ""],
      email: [
        "",
        [Validators.required, Validators.email, this.dotInUsernameValidator()],
      ],
    });
    this.form.valueChanges.subscribe(() => {
      if (this.form.dirty) {
        this.formStateService.setFormDirty();
      }
    });
    this.userId = localStorage.getItem("userId");
  }

  dotInUsernameValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const hasDot = control.value ? control.value.includes(".") : false;
      return hasDot ? null : { noDotInUsername: true };
    };
  }

  toggleDisable() {
    if (this.form.disabled) {
      this.form.enable();
    } else {
      this.form.disable();
    }
  }

  onWriterChange() {
    this.isWriter = !this.isWriter;
    if (this.isWriter) {
      this.showRoleError = false;
    }
    if (
      !this.isAdmin &&
      !this.isPublisher &&
      !this.isReviewer &&
      !this.isWriter &&
      !this.isTranslator
    ) {
      this.showRoleError = true;
    }
  }

  onReviewerChange() {
    this.isReviewer = !this.isReviewer;
    if (this.isReviewer) {
      this.showRoleError = false;
    }
    if (
      !this.isAdmin &&
      !this.isPublisher &&
      !this.isReviewer &&
      !this.isWriter &&
      !this.isTranslator
    ) {
      this.showRoleError = true;
    }
  }

  onPublisherChange() {
    this.isPublisher = !this.isPublisher;
    if (this.isPublisher) {
      this.showRoleError = false;
    }
    if (
      !this.isAdmin &&
      !this.isPublisher &&
      !this.isReviewer &&
      !this.isWriter &&
      !this.isTranslator
    ) {
      this.showRoleError = true;
    }
  }

  onAdminChange() {
    this.isAdmin = !this.isAdmin;
    if (this.isAdmin) {
      this.showRoleError = false;
    }
    if (
      !this.isAdmin &&
      !this.isPublisher &&
      !this.isReviewer &&
      !this.isWriter &&
      !this.isTranslator
    ) {
      this.showRoleError = true;
    }
  }

  onTranslatorChange() {
    this.isTranslator = !this.isTranslator;
    if (this.isTranslator) {
      this.showRoleError = false;
    }
    if (
      !this.isAdmin &&
      !this.isPublisher &&
      !this.isReviewer &&
      !this.isWriter &&
      !this.isTranslator
    ) {
      this.showRoleError = true;
    }
  }

  onChange(event) {
    this.form.markAsDirty();
    this.state = event.currentValue;
  }

  // convenience getter for easy access to form fields
  get f() {
    return this.form.controls;
  }

  closeModal() {
    this.modalService.dismissAll();
  }

  onSubmit() {
    let self = this;
    this.submitted = true;
    // stop here if form is invalid
    if (this.form.invalid) return;

    if (
      !this.isAdmin &&
      !this.isPublisher &&
      !this.isReviewer &&
      !this.isWriter &&
      !this.isTranslator
    ) {
      this.showRoleError = true;
      return;
    }

    // submit API call
    var payload = this.form.value;
    self.logedInUserService.get(function (logedInUser) {});

    payload.status = this.state;
    payload.roleId = [
      this.isWriter && this.Role.Writer,
      this.isReviewer && this.Role.Reviewer,
      this.isPublisher && this.Role.Publisher,
      this.isAdmin && this.Role.Administrator,
      this.isTranslator && this.Role.Translator,
    ].filter(Boolean);

    // console.log("user payload", payload);
    if (self.userManagementId) {
      self.crudService.update(
        payload,
        function (response) {
          if (
            response.error &&
            response.error.error &&
            response.error.error.message
          ) {
            if (
              response.error.error.message.startsWith(
                "The `userManagement` instance is not valid. Details: `username` User already exists"
              )
            ) {
              self.toastr.error(
                "User name already exists",
                "Invalid Operation"
              );
            } else {
              self.toastr.error(
                response.error.error.message,
                "Invalid Operation"
              );
            }
          } else {
            self.toastr.success(
              "User has been updated successfully.",
              "Success"
            );
            self.getUserManagementData();
            self.getUserList();
            self.form.markAsPristine();
            self.formStateService.clearFormDirty();
            self.userManagementId = null;
            self.closeModal();
          }
        },
        "userManagements/" + self.userManagementId
      );
    } else {
      payload.emailVerified = false;
      self.crudService.post(
        payload,
        function (response) {
          if (
            response.error &&
            response.error.error &&
            response.error.error.message
          ) {
            if (
              response.error.error.message.startsWith(
                "The `userManagement` instance is not valid. Details: `username` User already exists"
              )
            ) {
              self.toastr.error(
                "User name already exists",
                "Invalid Operation"
              );
            } else {
              self.toastr.error(
                response.error.error.message,
                "Invalid Operation"
              );
            }
          } else {
            self.toastr.success(
              "User has been created successfully.",
              "Success"
            );
            self.getUserManagementData();
            self.getUserList();
            self.form.markAsPristine();
            self.formStateService.clearFormDirty();
            self.closeModal();
          }
        },
        "userManagements"
      );
    }
  }

  onReset() {
    this.formStateService.clearFormDirty();
    if (!this.userManagementId) {
      this.submitted = false;
      this.state = true;
      this.form.reset();
      this.isAdmin = false;
      this.isWriter = false;
      this.isReviewer = false;
      this.isPublisher = false;
      this.isTranslator = false;
    } else {
      this.submitted = false;
      this.state = this.currentUser.status;
      this.form.patchValue(this.currentUser);
      const roleIds: string[] = this.currentUser.roleId;
      const isWriter = roleIds.includes(this.Role.Writer);
      const isReviewer = roleIds.includes(this.Role.Reviewer);
      const isPublisher = roleIds.includes(this.Role.Publisher);
      const isAdmin = roleIds.includes(this.Role.Administrator);
      const isTranslator = roleIds.includes(this.Role.Translator);
      this.isWriter = isWriter;
      this.isReviewer = isReviewer;
      this.isPublisher = isPublisher;
      this.isAdmin = isAdmin;
      this.isTranslator = isTranslator;
    }
  }

  toggleForm(userManagement: any) {
    this.showForm = !this.showForm;
    if (userManagement) {
      this.userManagementId = userManagement.id;
      this.state = userManagement.status;
      const roleIds: string[] = userManagement.roleId;
      const isWriter = roleIds.includes(this.Role.Writer);
      const isReviewer = roleIds.includes(this.Role.Reviewer);
      const isPublisher = roleIds.includes(this.Role.Publisher);
      const isAdmin = roleIds.includes(this.Role.Administrator);
      const isTranslator = roleIds.includes(this.Role.Translator);
      this.form.patchValue(userManagement);
      this.isWriter = isWriter;
      this.isReviewer = isReviewer;
      this.isPublisher = isPublisher;
      this.isAdmin = isAdmin;
      this.isTranslator = isTranslator;
    }
  }

  getUserList() {
    let self = this;
    self.logedInUserService.get(function (logedInUser) {
      self.crudService.get(
        null,
        function (response) {
          if (
            response.error &&
            response.error.error &&
            response.error.error.message
          ) {
            self.userList = [];
          } else {
            var otherUsers = response.filter(function (user) {
              return user.id !== logedInUser.id;
            });

            if (otherUsers && otherUsers.length) {
              self.userList = otherUsers.map(function (user) {
                return {
                  id: user.id,
                  username: user.username,
                };
              });
            } else {
              self.userList = [];
            }
          }
        },
        'userManagements?filter={"where":{"companyId":"' +
          logedInUser.companyId +
          '"}}'
      );
    });
  }

  openDeleteConfirmation(um) {
    this.logedInUserService.get((logedInUser) => {
      this.crudService.get(
        null,
        (pResponse) => {
          if (pResponse && pResponse.id) {
            if (pResponse.emailId == um.email) {
              this.openRootUser(um);
            } else if (logedInUser.email == um.email) {
              this.toastr.error(
                "You cannot delete your own account while logged in",
                "Deletion failed"
              );
            } else {
              this.openDeleteConfirmationDialog(um.id);
            }
          } else {
            this.openDeleteConfirmationDialog(um.id);
          }
        },
        "companyRegistrations/" + um.companyId
      );
    });
  }

  openRootUser(um) {
    var self = this;
    Swal.fire({
      title:
        '<div class="d-flex justify-content-between ">' +
        '<div class="pull-left" style="text-align: left;">' +
        this.translateService.instant("Form.ChangeRootUser") +
        "</div>" +
        "</div>",
      html:
        '<div style="display: flex; align-items: left;">' +
        '  <span for="replacedUser" >' +
        this.translateService.instant("Form.SelectRootUser") +
        ":</span>" +
        "</div>" +
        '<div style="display: flex; align-items: left;">' +
        '  <select id="replacedUser" class="swal2-select" style="width: 100%">' +
        "<option value=''>" +
        this.translateService.instant("Form.SelectUser") +
        "</option>" +
        `${
          this.userList
            ? this.userList
                .map(
                  (option) =>
                    `<option value='${option.id}'>${option.username}</option>`
                )
                .join("")
            : ""
        }` +
        "  </select>" +
        "</div>",
      confirmButtonText: this.translateService.instant("Form.Proceed"),
      showCancelButton: true,
      showCloseButton: true,

      customClass: {
        popup: "custom-popup-class",
        confirmButton: "swal2-confirm confirmDeleteProcced",
        closeButton: "custom-close-button",
      },
      showClass: {
        popup: "animated fadeInLeft faster",
      },
      allowOutsideClick: false,
      preConfirm: () => {
        const replacedUser = document.getElementById(
          "replacedUser"
        ) as HTMLSelectElement;

        const replaceUserId = replacedUser ? replacedUser.value : "";

        if (!replaceUserId) {
          Swal.showValidationMessage(
            this.translateService.instant("Form.Pleaseselectrootuser")
          );
        }

        return { replaceUserId };
      },
    }).then((result) => {
      if (result.isConfirmed) {
        const { replaceUserId } = result.value;
        var payload = { deleteUserId: um.id, replaceUserId: replaceUserId };
        self.crudService.post(
          payload,
          function (response) {
            if (
              response.error &&
              response.error.error &&
              response.error.error.message
            ) {
              self.toastr.error(
                response.error.error.message,
                "Invalid Operation"
              );
            } else {
              self.toastr.success(
                "User has been deleted successfully.",
                "Success"
              );
              self.getUserManagementData();
              self.getUserList();
            }
          },
          "userManagements/deleteRootUser"
        );
      }
    });
  }

  openDeleteConfirmationDialog(id): void {
    const dialogRef = this.dialog.open(DeleteConfirmationDialogComponent, {
      width: "600px",
      panelClass: "custom-bg",
      data: {
        title: this.translateService.instant("Form.DeleteUser"),
        paragraph: this.translateService.instant("Form.DeleteUserMsg"),
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.crudService.delete(
          null,
          (response) => {
            if (
              response.error &&
              response.error.error &&
              response.error.error.message
            ) {
              this.toastr.error(
                response.error.error.message,
                "Invalid Operation"
              );
            } else {
              this.toastr.success(
                "User has been deleted successfully.",
                "Success"
              );
              this.getUserManagementData();
              this.getUserList();
            }
          },
          "userManagements/" + id
        );
      }
    });
  }
  createUserManagement() {
    this.userManagementId = undefined;
    this.state = false;

    this.submitted = false;
    this.form.reset();
    this.showForm = true;
  }
}
